import { useEffect, useState } from 'react';

import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { CheckOutline, SavingsProductConfig, useToast } from '@aslanpay/portal-common-lib';

import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';

import { SavingsConfigModalProps } from './SavingsConfigModal.types';

const DEFAULT_FLEXIBLE_SAVER_RATE = 2;
const DEFAULT_CHALLENGE_SAVER_RATE = 4;
const MIN_ANNUAL_RATE = 0;
const MAX_ANNUAL_RATE = 100;

interface ProductSectionProps {
  title: string;
  description: string;
  testIdPrefix: string;
  enabled: boolean;
  rate: string;
  onEnabledChange: (enabled: boolean) => void;
  onRateChange: (rate: string) => void;
}

function ProductSection({
  title,
  description,
  testIdPrefix,
  enabled,
  rate,
  onEnabledChange,
  onRateChange,
}: ProductSectionProps) {
  return (
    <VStack align="stretch" spacing="12px">
      <Flex w="full" align="center" justify="space-between" gap="15px">
        <Flex flexDir="column">
          <Text textStyle="smallMedium" color="brand.black">
            {title}
          </Text>
          <Text textStyle="extraSmallRegular" color="brand.darkGray">
            {description}
          </Text>
        </Flex>
        <Switch
          colorScheme="badge.invited"
          size="lg"
          isChecked={enabled}
          onChange={(event) => onEnabledChange(event.target.checked)}
          aria-label={`${title} enabled`}
          data-testid={`${testIdPrefix}-enabled`}
        />
      </Flex>
      <FormControl>
        <FormLabel>Annual rate (%)</FormLabel>
        <Input
          type="number"
          min={MIN_ANNUAL_RATE}
          max={MAX_ANNUAL_RATE}
          step="0.01"
          value={rate}
          onChange={(event) => onRateChange(event.target.value)}
          data-testid={`${testIdPrefix}-rate`}
        />
        <FormHelperText>
          Annual interest rate applied to this product, between 0 and 100.
        </FormHelperText>
      </FormControl>
    </VStack>
  );
}

function SavingsConfigModal({
  isOpen,
  onClose,
  companyId,
  initialConfig = undefined,
}: SavingsConfigModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: updateCompanyConfigurationAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const initialFlexibleEnabled = initialConfig?.flexibleSaverConfig?.enabled ?? false;
  const initialChallengeEnabled = initialConfig?.challengeSaverConfig?.enabled ?? false;
  const initialFlexibleRate = String(
    initialConfig?.flexibleSaverConfig?.annualRate ?? DEFAULT_FLEXIBLE_SAVER_RATE
  );
  const initialChallengeRate = String(
    initialConfig?.challengeSaverConfig?.annualRate ?? DEFAULT_CHALLENGE_SAVER_RATE
  );

  const [flexibleEnabled, setFlexibleEnabled] = useState(initialFlexibleEnabled);
  const [challengeEnabled, setChallengeEnabled] = useState(initialChallengeEnabled);
  const [flexibleRate, setFlexibleRate] = useState(initialFlexibleRate);
  const [challengeRate, setChallengeRate] = useState(initialChallengeRate);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFlexibleEnabled(initialFlexibleEnabled);
      setChallengeEnabled(initialChallengeEnabled);
      setFlexibleRate(initialFlexibleRate);
      setChallengeRate(initialChallengeRate);
    }
  }, [
    isOpen,
    initialFlexibleEnabled,
    initialChallengeEnabled,
    initialFlexibleRate,
    initialChallengeRate,
  ]);

  const isDirty =
    flexibleEnabled !== initialFlexibleEnabled ||
    challengeEnabled !== initialChallengeEnabled ||
    flexibleRate !== initialFlexibleRate ||
    challengeRate !== initialChallengeRate;

  const isRateValid = (rate: string) => {
    const parsed = Number(rate);
    return (
      rate.trim() !== '' &&
      Number.isFinite(parsed) &&
      parsed >= MIN_ANNUAL_RATE &&
      parsed <= MAX_ANNUAL_RATE
    );
  };

  const isValid = isRateValid(flexibleRate) && isRateValid(challengeRate);

  const handleClose = () => {
    if (isDirty) {
      setIsDiscardOpen(true);
      return;
    }
    onClose();
  };

  const handleDiscard = () => {
    setIsDiscardOpen(false);
    setFlexibleEnabled(initialFlexibleEnabled);
    setChallengeEnabled(initialChallengeEnabled);
    setFlexibleRate(initialFlexibleRate);
    setChallengeRate(initialChallengeRate);
    onClose();
  };

  const handleSave = async () => {
    if (!isDirty || !isValid) return;

    const flexibleSaverConfig: SavingsProductConfig = {
      enabled: flexibleEnabled,
      annualRate: Number(flexibleRate),
    };
    const challengeSaverConfig: SavingsProductConfig = {
      enabled: challengeEnabled,
      annualRate: Number(challengeRate),
    };

    setIsSaving(true);
    try {
      await updateCompanyConfigurationAsync({
        id: companyId,
        savingsFeatureConfig: { flexibleSaverConfig, challengeSaverConfig },
      });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The savings configuration was successfully saved.',
      });

      queryClient.invalidateQueries({ queryKey: ['fetchCompanyConfiguration', companyId] });
      onClose();
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the savings configuration.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Savings settings</ModalHeader>
          <ModalCloseButton data-testid="savings-modal-close" />
          <ModalBody>
            <VStack align="stretch" spacing="20px">
              <ProductSection
                title="Flexible Saver"
                description="Let employees save flexibly and earn interest at the rate below."
                testIdPrefix="flexible-saver"
                enabled={flexibleEnabled}
                rate={flexibleRate}
                onEnabledChange={setFlexibleEnabled}
                onRateChange={setFlexibleRate}
              />
              <Divider />
              <ProductSection
                title="Challenge Saver"
                description="Let employees lock savings into a challenge and earn interest at the rate below."
                testIdPrefix="challenge-saver"
                enabled={challengeEnabled}
                rate={challengeRate}
                onEnabledChange={setChallengeEnabled}
                onRateChange={setChallengeRate}
              />
            </VStack>
          </ModalBody>
          <ModalFooter gap="10px">
            <Button variant="outline" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              leftIcon={<CheckOutline boxSize="20px" />}
              size="sm"
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={!isDirty || !isValid}
              data-testid="save-savings-button"
            >
              Save changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDiscardOpen} onClose={() => setIsDiscardOpen(false)} isCentered size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Discard changes?</ModalHeader>
          <ModalBody>
            <Text textStyle="smallRegular" color="brand.darkGray">
              You have unsaved changes. Are you sure you want to discard them?
            </Text>
          </ModalBody>
          <ModalFooter gap="10px">
            <Button variant="outline" size="sm" onClick={() => setIsDiscardOpen(false)}>
              Keep editing
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDiscard}
              data-testid="discard-savings-button"
            >
              Discard
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export { SavingsConfigModal };
